import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";
import { saveNodeType, isHover } from "../utils";

import { getNewXY } from "./EachTrendLine";
import StraightLine from "../components/StraightLine";
import ClickableCircle from "../components/ClickableCircle";
import PitchforkLines from "../components/PitchforkLines";
import HoverTextNearMouse from "../components/HoverTextNearMouse";

class EachAndrewsPitchfork extends Component {
  constructor(props) {
    super(props);

    this.handlePoint0Drag = this.handlePoint0Drag.bind(this);
    this.handlePoint1Drag = this.handlePoint1Drag.bind(this);
    this.handlePoint2Drag = this.handlePoint2Drag.bind(this);

    this.handleLineMove = this.handleLineMove.bind(this);
    this.handleLineDragStart = this.handleLineDragStart.bind(this);

    this.handleHover = this.handleHover.bind(this);

    this.isHover = isHover.bind(this);
    this.saveNodeType = saveNodeType.bind(this);
    this.nodes = {};

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleLineDragStart() {
    const { x0, y0, x1, y1, x2, y2 } = this.props;

    this.dragStart = {
      x0,
      y0,
      x1,
      y1,
      x2,
      y2,
    };
  }

  handleLineMove(moreProps) {
    const { index, onDrag } = this.props;

    const {
      x0: x0Value,
      y0: y0Value,
      x1: x1Value,
      y1: y1Value,
      x2: x2Value,
      y2: y2Value,
    } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
    } = moreProps;
    const { startPos, mouseXY } = moreProps;

    const x0 = xScale(x0Value);
    const y0 = yScale(y0Value);
    const x1 = xScale(x1Value);
    const y1 = yScale(y1Value);
    const x2 = xScale(x2Value);
    const y2 = yScale(y2Value);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    const newX0Value = getXValue(
      xScale,
      xAccessor,
      [x0 - dx, y0 - dy],
      fullData,
    );
    const newY0Value = yScale.invert(y0 - dy);
    const newX1Value = getXValue(
      xScale,
      xAccessor,
      [x1 - dx, y1 - dy],
      fullData,
    );
    const newY1Value = yScale.invert(y1 - dy);
    const newX2Value = getXValue(
      xScale,
      xAccessor,
      [x2 - dx, y2 - dy],
      fullData,
    );
    const newY2Value = yScale.invert(y2 - dy);

    onDrag(index, {
      x0: newX0Value,
      y0: newY0Value,
      x1: newX1Value,
      y1: newY1Value,
      x2: newX2Value,
      y2: newY2Value,
    });
  }

  handlePoint0Drag(moreProps) {
    const [x0, y0] = getNewXY(moreProps);
    const { onPoint0Drag } = this.props;
    onPoint0Drag([x0, y0]);
  }

  handlePoint1Drag(moreProps) {
    const [x1, y1] = getNewXY(moreProps);
    const { onPoint1Drag } = this.props;
    onPoint1Drag([x1, y1]);
  }

  handlePoint2Drag(moreProps) {
    const [x2, y2] = getNewXY(moreProps);
    const { onPoint2Drag } = this.props;
    onPoint2Drag([x2, y2]);
  }

  render() {
    const { x0, x1, x2, y0, y1, y2 } = this.props;
    const { interactive, appearance } = this.props;

    // Don't render if missing first two points (at minimum)
    if (x0 === null || x1 === null) return null;

    // x2 can be null during drawing (after first drag, before third click)
    const hasThirdPoint = x2 !== null && y2 !== null;

    // Extract values from registry format consistently
    const stroke = appearance.stroke?.color || "#FF6B6B";
    const strokeWidth = appearance.stroke?.width || 2;
    const strokeOpacity =
      appearance.stroke?.opacity !== undefined ? appearance.stroke.opacity : 1;
    const strokeDasharray = appearance.stroke?.dash || "Solid";

    const { r } = appearance;

    // Get theme-aware edge colors for maximum visibility
    const edgeColors = {
      stroke: "#000000", // Black stroke
      fill: "#FFFFFF", // White fill
    };

    const actualEdgeFill = edgeColors.fill;
    // Force larger radius for better visibility
    const circleRadius = r || 6;

    const { hoverText, selected } = this.props;
    const { hover } = this.state;
    const { onDragComplete } = this.props;

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    return (
      <g>
        {/* Preview line during initial drag (p0 → p1) before third point is set */}
        {!hasThirdPoint && x1 !== null && (
          <StraightLine
            ref={this.saveNodeType("preview")}
            selected={false}
            type="LINE"
            x1Value={x0}
            y1Value={y0}
            x2Value={x1}
            y2Value={y1}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeOpacity={strokeOpacity * 0.7}
            strokeDasharray={strokeDasharray}
            interactiveCursorClass="react-stockcharts-move-cursor"
            onDragStart={this.handleLineDragStart}
            onDrag={this.handleLineMove}
            onDragComplete={onDragComplete}
          />
        )}

        {/* Render full pitchfork rays after third point is set */}
        {hasThirdPoint && (
          <PitchforkLines
            ref={this.saveNodeType("pitchfork")}
            selected={selected || hover}
            onHover={this.handleHover}
            onUnHover={this.handleHover}
            x0={x0}
            y0={y0}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke={stroke}
            strokeWidth={hover || selected ? strokeWidth + 1 : strokeWidth}
            strokeDasharray={strokeDasharray}
            strokeOpacity={strokeOpacity}
            interactiveCursorClass="react-stockcharts-move-cursor"
            onDragStart={this.handleLineDragStart}
            onDrag={this.handleLineMove}
            onDragComplete={onDragComplete}
          />
        )}

        {/* Drag handles at anchor points - show only when hovering/selected */}
        {interactive && (hover || selected) && hasThirdPoint && (
          <g>
            <ClickableCircle
              ref={this.saveNodeType("point0")}
              show={true}
              cx={x0}
              cy={y0}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint0Drag}
              onDragComplete={onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point1")}
              show={true}
              cx={x1}
              cy={y1}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint1Drag}
              onDragComplete={onDragComplete}
            />
            <ClickableCircle
              ref={this.saveNodeType("point2")}
              show={true}
              cx={x2}
              cy={y2}
              r={circleRadius}
              fill={actualEdgeFill}
              stroke={edgeColors.stroke}
              strokeWidth={2}
              strokeOpacity={1}
              interactiveCursorClass="react-stockcharts-move-cursor"
              onDrag={this.handlePoint2Drag}
              onDragComplete={onDragComplete}
            />
          </g>
        )}

        {/* Hover tooltip */}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

EachAndrewsPitchfork.propTypes = {
  x0: PropTypes.number,
  y0: PropTypes.number,
  x1: PropTypes.number,
  y1: PropTypes.number,
  x2: PropTypes.number,
  y2: PropTypes.number,

  index: PropTypes.number,
  interactive: PropTypes.bool,
  selected: PropTypes.bool,

  appearance: PropTypes.shape({
    stroke: PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      dash: PropTypes.string,
      opacity: PropTypes.number,
    }),
    r: PropTypes.number,
  }).isRequired,

  hoverText: PropTypes.object.isRequired,

  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  onPoint0Drag: PropTypes.func,
  onPoint1Drag: PropTypes.func,
  onPoint2Drag: PropTypes.func,
};

EachAndrewsPitchfork.defaultProps = {
  interactive: true,
  selected: false,
  onDrag: noop,
  onDragComplete: noop,
  onPoint0Drag: noop,
  onPoint1Drag: noop,
  onPoint2Drag: noop,
  hoverText: {
    enable: false,
  },
};

export default EachAndrewsPitchfork;
